/**
 * Memory Page enrichment helpers — pure data extraction.
 *
 * Voice-free and framework-free by design. Personas import these from
 * `personas.ts` to compose the rich Memory Page content; nothing in
 * here knows about Morwenna / Jago / Demelza or about React.
 *
 * Why a separate module:
 *   - Testability: extraction logic is unit-testable in isolation when
 *     test infra arrives, without spinning up a persona.
 *   - Reuse: when the API ships and chat history moves server-side, the
 *     same extraction primitives apply to server-fetched history.
 *   - Boundary clarity: data extraction here, voice composition in
 *     `personas.ts`, layout in `MemoryPage.tsx`. Each file has one job.
 */
import type { ChatMessage } from '../components/ChatBubble';
import { formatStayDate, type Profile } from './onboarding';

/**
 * Per-day rollup of intents the user touched. Voice-free — personas
 * transform this into a one-liner summary in their own voice via
 * `Persona.composeMemoryPage`.
 *
 * Flag-setting (Batch 13 / 14):
 *   - `askedAboutWeather` and `askedAboutFood` fire on EITHER a system-
 *     tagged message kind (the auto 4pm teatime nudge / weather push)
 *     OR a user-typed message whose text matches the relevant keyword
 *     pattern. Pre-Batch-13 only the kind path existed, which meant
 *     a user typing "what's for tea tonight?" never flipped the flag
 *     and every day read as "we had a chat".
 *   - `askedAboutTides`, `askedAboutWalks`, `askedAboutBeach` are
 *     user-keyword-only — there are no system-tagged kinds for these.
 *
 * Naming (Batch 14):
 *   - `askedAboutFood` was `askedAboutTea` in Batch 13. Renamed
 *     because the keyword pattern matches broader eating-out queries
 *     (brunch, cafe, coffee, pub) and "tea" — strictly the British
 *     evening meal — was misleading as a surface noun in the
 *     keepsake. The kind path stays correct (the 4pm teatime nudge
 *     is still a food/eating moment).
 *   - `askedAboutWalks` was `askedAboutDogWalk` in Batch 13. The
 *     previous DOG keyword matched the literal word "dog" in
 *     onboarding selections like "Couple + dog", firing a false
 *     positive for users who never asked about walks. WALKS replaces
 *     it with movement-related vocabulary (walk / hike / trail / coast
 *     path / exercise) — captures the real intent and ignores
 *     onboarding answers.
 *
 * Keyword detection is intentionally generous: a false positive on a
 * topic mentioned in passing produces a slightly broader-than-truth
 * day summary, which is much softer than the false-negative failure
 * mode (every day reading "we had a chat" regardless of what was
 * actually discussed).
 */
export interface DayActivity {
  /** YYYY-MM-DD (local). */
  readonly dateLocal: string;
  readonly askedAboutWeather: boolean;
  readonly askedAboutFood: boolean;
  readonly askedAboutTides: boolean;
  readonly askedAboutWalks: boolean;
  readonly askedAboutBeach: boolean;
  /** Distinct venues the user picked on this day, deduplicated by
   *  case-insensitive name. Order is order-of-first-occurrence. */
  readonly venuesPicked: ReadonlyArray<string>;
  readonly updatedProfile: boolean;
}

/** A day in the user's history with metadata for grouping. */
export interface DayEntry {
  readonly dateLocal: string;
  readonly label: string;
  readonly messageCount: number;
}

/** A venue the user acknowledged picking, deduplicated across the week. */
export interface PlaceEntry {
  /** Canonical name from the original Pick (case preserved). */
  readonly name: string;
  /** ISO date YYYY-MM-DD when first picked. */
  readonly firstDateLocal: string;
}

const WEEKDAY_LABELS: ReadonlyArray<string> = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

// ─────────────────────────────────────────────────────────────────────
// User-text keyword patterns for day-summary intent detection
// (Batch 13, refined 14).
//
// Each pattern matches user-typed phrasings that signal interest in a
// topic. Word-boundary anchors avoid substring matches ("teaching"
// won't fire FOOD, "Sandy" won't fire BEACH, etc). Compiled once at
// module scope so `summariseDay` doesn't recompile per message.
//
// Patterns lean generous: missing a real intent silently broke the
// keepsake for months (the "we had a chat" bug); occasional false
// positives on incidental mentions are far less harmful. The Batch
// 14 rename from DOG to WALKS was driven by the opposite failure
// mode — the DOG keyword matched the literal word in onboarding
// selections ("Couple + dog"), firing for users who never asked
// about walks. WALKS captures the real intent without that false
// positive.
// ─────────────────────────────────────────────────────────────────────
const WEATHER_KEYWORDS =
  /\b(weather|forecast|rain|raining|rains|sun|sunny|wind|windy|cloudy|cloud|clouds|hot|cold|warm|cool|temperature|degrees)\b/i;
const FOOD_KEYWORDS =
  /\b(tea|dinner|breakfast|lunch|brunch|eat|eating|food|restaurant|restaurants|pub|pubs|cafe|cafes|coffee|takeaway|chippy|chip shop|fish and chips|hungry)\b/i;
const TIDES_KEYWORDS =
  /\b(tide|tides|low water|high water|low tide|high tide)\b/i;
const WALKS_KEYWORDS =
  /\b(walk|walks|walking|hike|hiking|trail|trails|coast path|stroll|stretch the legs|exercise)\b/i;
const BEACH_KEYWORDS =
  /\b(beach|beaches|sand|swim|swimming|surf|surfing|rockpool|rockpools|rockpooling|paddle|paddling)\b/i;

/**
 * Parse a YYYY-MM-DD string as a local Date (midnight in the user's
 * timezone). NOT `new Date(iso)` — that parses as UTC and rolls back
 * a day for users west of UTC after sundown. Same lesson as
 * `isoDateLocal` in onboarding.ts: always use local components for
 * date-only ISO strings.
 */
function localDateFromIso(iso: string): Date {
  const parts = iso.split('-').map((s) => Number.parseInt(s, 10));
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  return new Date(y, m - 1, d);
}

/**
 * Human label for a date in the context of a stay. Within the stay
 * window we show the weekday name ("Wednesday") because that's how
 * holidaymakers think about their week. Outside the window we fall
 * back to the formatted date ("7 May") to avoid weekday ambiguity for
 * messages that landed before/after the booked range.
 */
export function dayLabel(dateLocal: string, profile: Profile): string {
  if (
    dateLocal >= profile.stayStart &&
    dateLocal <= profile.stayEnd
  ) {
    const dayIndex = localDateFromIso(dateLocal).getDay();
    return WEEKDAY_LABELS[dayIndex] ?? formatStayDate(dateLocal);
  }
  return formatStayDate(dateLocal);
}

/**
 * Group history into per-day entries, sorted chronologically ascending.
 * Excludes 'seed' kind defensively — seed messages aren't supposed to
 * be persisted (filtered by id elsewhere) but if one ever slips through
 * we don't want it appearing in the timeline.
 *
 * YYYY-MM-DD strings sort lexicographically === chronologically, so a
 * plain `localeCompare` is correct.
 */
export function groupHistoryByDay(
  history: ReadonlyArray<ChatMessage>,
  profile: Profile,
): DayEntry[] {
  const counts = new Map<string, number>();
  for (const m of history) {
    if (m.kind === 'seed') continue;
    counts.set(m.dateLocal, (counts.get(m.dateLocal) ?? 0) + 1);
  }
  const entries: DayEntry[] = [];
  for (const [dateLocal, messageCount] of counts) {
    entries.push({
      dateLocal,
      label: dayLabel(dateLocal, profile),
      messageCount,
    });
  }
  return entries.sort((a, b) => a.dateLocal.localeCompare(b.dateLocal));
}

/**
 * Roll a single day's messages into a voice-free DayActivity. Caller
 * (persona daySummary) decides how to phrase it. Boolean flags so
 * personas can branch cheaply; venuesPicked deduplicated case-insensitively
 * because the same venue picked twice in a day shouldn't double up.
 *
 * Two parallel detection paths per message (Batch 13 / 14):
 *   1. Kind-based: existing flag-setting on `m.kind === 'weather' |
 *      'teatime' | 'profile-update' | 'pick-ack'`. These are system-
 *      tagged message kinds (the 4pm nudge, the weather card, etc).
 *   2. User-text keyword: when `m.from === 'user'`, the typed text is
 *      pattern-matched against WEATHER / FOOD / TIDES / WALKS / BEACH
 *      keyword lists. Flags fire on either path (OR semantics), so
 *      the user typing "what's for tea?" and the system pushing a
 *      teatime card both set `askedAboutFood`.
 */
export function summariseDay(
  history: ReadonlyArray<ChatMessage>,
  dateLocal: string,
): DayActivity {
  let askedAboutWeather = false;
  let askedAboutFood = false;
  let askedAboutTides = false;
  let askedAboutWalks = false;
  let askedAboutBeach = false;
  let updatedProfile = false;
  const venues: string[] = [];
  const seenVenues = new Set<string>();

  for (const m of history) {
    if (m.dateLocal !== dateLocal) continue;
    if (m.kind === 'seed') continue;

    // Path 1 — system-tagged kinds. Mutually exclusive (a message has
    // one kind), so chained if/else if matches the original semantics.
    if (m.kind === 'weather') askedAboutWeather = true;
    else if (m.kind === 'teatime') askedAboutFood = true;
    else if (m.kind === 'profile-update') updatedProfile = true;
    else if (m.kind === 'pick-ack' && m.pickName) {
      const key = m.pickName.toLowerCase().trim();
      if (key.length > 0 && !seenVenues.has(key)) {
        seenVenues.add(key);
        venues.push(m.pickName);
      }
    }

    // Path 2 — user-text keyword detection (Batch 13). Independent of
    // kind; ORs with the kind-based flags above. Skips persona messages
    // so Ollie/Morwenna mentioning a topic in their reply doesn't
    // accidentally fire the flag — only what the user themselves
    // typed counts as "asked about".
    if (m.from === 'user') {
      if (WEATHER_KEYWORDS.test(m.text)) askedAboutWeather = true;
      if (FOOD_KEYWORDS.test(m.text)) askedAboutFood = true;
      if (TIDES_KEYWORDS.test(m.text)) askedAboutTides = true;
      if (WALKS_KEYWORDS.test(m.text)) askedAboutWalks = true;
      if (BEACH_KEYWORDS.test(m.text)) askedAboutBeach = true;
    }
  }

  return {
    dateLocal,
    askedAboutWeather,
    askedAboutFood,
    askedAboutTides,
    askedAboutWalks,
    askedAboutBeach,
    venuesPicked: venues,
    updatedProfile,
  };
}

/**
 * Extract pick-ack venues across the full history, deduplicated
 * case-insensitively by name. Earliest dateLocal wins — a venue
 * mentioned twice in a week appears once, on the day they first picked
 * it (the "discovery" date reads more meaningfully than a re-mention).
 *
 * Sorted ascending by date so the rendered list reads in the order
 * the user's week unfolded.
 */
export function extractPickAcks(
  history: ReadonlyArray<ChatMessage>,
): PlaceEntry[] {
  const seen = new Map<string, PlaceEntry>();
  for (const m of history) {
    if (m.kind !== 'pick-ack') continue;
    if (!m.pickName) continue;
    const key = m.pickName.toLowerCase().trim();
    if (key.length === 0) continue;
    const existing = seen.get(key);
    if (!existing) {
      seen.set(key, { name: m.pickName, firstDateLocal: m.dateLocal });
    } else if (m.dateLocal < existing.firstDateLocal) {
      seen.set(key, { name: existing.name, firstDateLocal: m.dateLocal });
    }
  }
  return Array.from(seen.values()).sort((a, b) =>
    a.firstDateLocal.localeCompare(b.firstDateLocal),
  );
}

/**
 * Oxford-comma list join. "X" → "X"; "X, Y" → "X and Y"; "X, Y, Z" →
 * "X, Y, and Z". Used by persona daySummary helpers to render
 * multi-venue lines without each persona reimplementing the same
 * little formatting logic.
 *
 * Empty input returns empty string. Caller is responsible for guarding
 * the empty case before composing into a sentence.
 */
export function listAnd(items: ReadonlyArray<string>): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items.join('');
  if (items.length === 2) return items.join(' and ');
  // 3+: pop the last item from a copy so we can join the head with
  // commas and append " and {last}". The spread/pop pattern avoids
  // the noUncheckedIndexedAccess undefined dance.
  const arr = [...items];
  const last = arr.pop() ?? '';
  return `${arr.join(', ')}, and ${last}`;
}
